import type {
  AdminRating,
  AdminTenant,
  AdminUser,
  CreateTenantInput,
  UpdateTenantInput,
  UpdateUserRoleInput,
} from '@lms/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { PaginatedResponse, Pagination } from '@/lib/api'
import { api } from '@/lib/api'

export type { PaginatedResponse, Pagination }

interface UserFilters {
  page?: string
  limit?: string
  search?: string
  role?: string
}

export function useAdminUsers(filters?: UserFilters) {
  return useQuery({
    queryKey: ['admin', 'users', filters],
    queryFn: () => api.getFull<PaginatedResponse<AdminUser>>('/api/admin/users', filters),
  })
}

export function useUpdateUserRole() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: UpdateUserRoleInput['role'] }) =>
      api.patch<AdminUser>(`/api/admin/users/${userId}/role`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

// Tenants
export function useAdminTenants() {
  return useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => api.getFull<PaginatedResponse<AdminTenant>>('/api/admin/tenants'),
  })
}

export function useAdminTenant(id: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'tenants', id],
    queryFn: () => api.get<AdminTenant>(`/api/admin/tenants/${id}`),
    enabled: !!id,
  })
}

export function useCreateTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateTenantInput) => api.post<AdminTenant>('/api/admin/tenants', data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'tenants'] }),
  })
}

export function useUpdateTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTenantInput }) =>
      api.patch<AdminTenant>(`/api/admin/tenants/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'tenants'] }),
  })
}

export function useDeleteTenant() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete<AdminTenant>(`/api/admin/tenants/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'tenants'] }),
  })
}

// Enrollments — search & detail

interface SearchUsersFilters {
  name: string
  page?: string
  limit?: string
}

export function useAdminSearchUsers(filters: SearchUsersFilters | null) {
  return useQuery({
    queryKey: ['admin', 'users', 'search', filters],
    queryFn: () =>
      api.get('/api/admin/enrollments/users/search', filters as unknown as Record<string, string>),
    enabled: !!filters?.name,
  })
}

interface SearchCoursesFilters {
  title: string
  page?: string
  limit?: string
}

export function useAdminSearchCourses(filters: SearchCoursesFilters | null) {
  return useQuery({
    queryKey: ['admin', 'courses', 'search', filters],
    queryFn: () =>
      api.get(
        '/api/admin/enrollments/courses/search',
        filters as unknown as Record<string, string>,
      ),
    enabled: !!filters?.title,
  })
}

export function useAdminUserEnrollments(userId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'enrollments', 'user', userId],
    queryFn: () => api.get(`/api/admin/enrollments/user/${userId}`),
    enabled: !!userId,
  })
}

export function useAdminCourseEnrollments(courseId: string | undefined) {
  return useQuery({
    queryKey: ['admin', 'enrollments', 'course', courseId],
    queryFn: () => api.get(`/api/admin/enrollments/course/${courseId}`),
    enabled: !!courseId,
  })
}

// Ratings
interface RatingFilters {
  page?: string
  limit?: string
  lessonId?: string
  userId?: string
}

export function useAdminRatings(filters?: RatingFilters) {
  return useQuery({
    queryKey: ['admin', 'ratings', filters],
    queryFn: () => api.getFull<PaginatedResponse<AdminRating>>('/api/admin/ratings', filters),
  })
}

export function useExportRatingsCsv() {
  return useMutation({
    mutationFn: async (filters?: RatingFilters) => {
      const res = await api.raw('/api/admin/ratings/export', filters)
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ratings-${new Date().toISOString().slice(0, 10)}.csv`
      a.click()
      URL.revokeObjectURL(url)
    },
  })
}
